import classnames from "classnames";
import { defer, first, flow, isNil } from "lodash";
import { toggleClass } from "oc-ui/Doms";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import React, { useEffect, useState } from "react";

export function Tabs({
  children,
  tabsKey,
  className,
  defaultActiveKey = null,
  activeTabKey,
  setActiveTabKey,
  fadedIn = true,
  showToggleButton = false,
  ...args
}: {
  children?: JSX.Element[];
  tabsKey?: string;
  className?: string;
  defaultActiveKey?: string;
  activeTabKey?: string;
  setActiveTabKey?: (value: string) => void;
  fadedIn?: boolean;
  showToggleButton?: boolean;
  [x: string]: any;
}) {
  const [isSlideOut, setIsSlideOut] = useState(false);

  if (!setActiveTabKey) {
    [activeTabKey, setActiveTabKey] = useState(defaultActiveKey);
    useEffect(() => {
      setActiveTabKey(defaultActiveKey);
    }, [defaultActiveKey]);
  }

  let _setFadedIn = (v) => {};
  let setFadedIn = (value) => {
    fadedIn = value;
  };

  [fadedIn, _setFadedIn] = useState(true);
  useEffect(() => {
    setFadedIn(true);
  }, [fadedIn]);

  const hasSlideOutClass = () => document.querySelector(".tabs")?.classList.contains("slide-out");

  const handleToggle = () => {
    const container = document.querySelector(".editor-optionen") as HTMLElement;
    toggleClass(container, "slide-out");
    setIsSlideOut(hasSlideOutClass());
  };

  const activeClassName = (child, className) =>
    classnames(
      className,
      activeTabKey == child.props.tabKey ? "active" : "",
      child.props.className
    );

  const getTabKey = (c) => c?.props?.tabKey;
  const tabs = (children.filter?.(flow(getTabKey, isNil, (bool) => !bool)) ?? [children]) as any[];
  const hasManyTabs = tabs.length > 3;
  const firstRowCount = hasManyTabs ? Math.ceil(tabs.length / 2) : tabs.length;
  const headerTabs = [...tabs];
  const isActive = flow(getTabKey, (k) => k == activeTabKey);
  if (tabs.length > 0 && !tabs.some(isActive)) {
    console.log(activeTabKey + " missing from " + tabs.map(getTabKey).join(", "));
    defer(() => flow(first, getTabKey, setActiveTabKey)(tabs));
  }

  return (
    <div className={classnames(className, "tabs")} {...args}>
      {showToggleButton && (
        <div className="toggle-menu">
          <div
            className="toggle-button"
            onClick={handleToggle}
            role="button"
            tabIndex={0}
            aria-pressed={isSlideOut}
            aria-label={I18N("aria::Seitenmenü ein- und ausklappen")}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleToggle();
              }
            }}
          >
            {hasSlideOutClass() ? <ExpandMore fontSize="large" /> : <ExpandLess fontSize="large" />}
          </div>
        </div>
      )}

      <div
        className={classnames("tab-headers", { "tab-headers--two-rows": hasManyTabs })}
        role="tablist"
        data-select-key={tabsKey || "Tab"}
        data-value={activeTabKey}
      >
        {headerTabs.map((tab, index) => {
          const isActive = activeTabKey === tab.props.tabKey;
          const tabKey = getTabKey(tab);
          const isSecondRow = hasManyTabs && index >= firstRowCount;
          const rowSize = hasManyTabs
            ? isSecondRow
              ? headerTabs.length - firstRowCount
              : firstRowCount
            : undefined;

          const activateTab = () => {
            flow(getTabKey, setActiveTabKey)(tab);
            APP.logUserAction({ action: "clickTab", tab: tabKey }, { stateChange: false });
          };

          const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              activateTab();
              return;
            }

            // optional mit Pfeiltasten zwischen Tabs wechseln
            if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
              event.preventDefault();
              const dir = event.key === "ArrowRight" ? 1 : -1;
              const currentIndex = index;
              let nextIndex = (currentIndex + dir + headerTabs.length) % headerTabs.length;
              const nextTab = headerTabs[nextIndex];
              const nextKey = getTabKey(nextTab);

              setActiveTabKey(nextKey);

              const headerNodes = document.querySelectorAll<HTMLElement>(
                ".tab-headers .tab-header"
              );
              if (headerNodes[nextIndex]) {
                headerNodes[nextIndex].focus();
              }
            }
          };

          return (
            <div
              key={tabKey}
              id={`tab-${tabKey}`}
              className={activeClassName(tab, "tab-header")}
              style={
                hasManyTabs
                  ? ({ ["--tab-row-size" as any]: rowSize } as React.CSSProperties)
                  : undefined
              }
              onClick={activateTab}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onKeyDown={handleKeyDown}
            >
              {/* Hidden radio so getCurrentUIState picks up available tab options */}
              <input
                type="radio"
                name={tabsKey || "Tab"}
                value={tabKey}
                checked={isActive}
                onChange={activateTab}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  width: 0,
                  height: 0,
                }}
                tabIndex={-1}
                aria-hidden="true"
              />
              {tab.props.header}
            </div>
          );
        })}
      </div>
      <div className="tab-bodies">
        {tabs.map((tab) => (
          <div
            key={tab.props.header}
            data-group-key={tab.props.header}
            className={activeClassName(tab, "tab-body")}
            role="tabpanel"
            aria-labelledby={`tab-${tab.props.tabKey}`}
            ref={tab.props.bodyRef}
          >
            {tab}
          </div>
        ))}
      </div>
    </div>
  );
}

// nur ein Stub, reiner Datencontainer, Darstellung erfolgt in der Tabs Klasse, WTF WHY?
// SBA: Nur damit man im JSX <Tab> schreiben kann. Ich hatte es anders probiert, aber war damit wegen der ref
// auf die Nase gefallen. Kann sein, dass das mit aktuellen React Versionen besser geht, aber so tut's halt..
export function Tab({
  children,
  tabKey,
  header,
  className,
}: {
  children: JSX.Element | JSX.Element[];
  tabKey?: string;
  header?: string;
  className?: string;
}) {
  return <React.Fragment>{children}</React.Fragment>;
}
